import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { colors } from '../styles/GlobalStyles';
import { configService } from '../services/configService';
import { paymentService } from '../services/paymentService';

// Inizializza Stripe con la chiave pubblica
const stripePromise = loadStripe(configService.getConfig().stripe.publicKey);

const StripeContainer = styled.div`
  background: ${colors.white};
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${colors.secondary};
  margin: 5px 0;
  box-shadow: none;
  
  @media (min-width: 768px) {
    padding: 30px;
    margin: 20px 0;
    border: 2px solid ${colors.secondary};
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const StripeTitle = styled.h3`
  color: ${colors.primary};
  margin-bottom: 15px;
  text-align: center;
  font-size: 1.2rem;
  line-height: 1.3;
  
  @media (min-width: 768px) {
    margin-bottom: 20px;
    font-size: 1.5rem;
  }
`;

const PaymentSummary = styled.div`
  background: transparent;
  padding: 6px 0;
  border-radius: 0;
  margin-bottom: 8px;
  border: none;
  box-shadow: none;
  
  @media (min-width: 768px) {
    background: ${colors.cream};
    padding: 20px;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1px solid ${colors.secondary};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
  padding: 1px 4px;
  font-size: 0.85rem;
  
  @media (min-width: 768px) {
    margin-bottom: 8px;
    padding: 5px 0;
    font-size: 1rem;
  }

  &:last-child {
    border-top: 1px solid ${colors.secondary};
    margin-top: 5px;
    padding-top: 5px;
    font-weight: bold;
    font-size: 0.95rem;
    
    @media (min-width: 768px) {
      border-top: 2px solid ${colors.secondary};
      margin-top: 10px;
      padding-top: 10px;
      font-size: 1.1rem;
    }
  }
`;

const SummaryLabel = styled.div`
  color: ${colors.brown};
  flex: 1;
  line-height: 1.2;
`;

const SummaryValue = styled.div`
  color: ${colors.primary};
  font-weight: bold;
  margin-left: 8px;
  flex-shrink: 0;
`;

const PaymentTypeSelector = styled.div`
  margin-bottom: 8px;
  
  @media (min-width: 768px) {
    margin-bottom: 20px;
  }
`;

const PaymentOption = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: flex-start;
  padding: 6px 8px;
  margin-bottom: 4px;
  border: 1px solid ${props => props.$checked ? colors.primary : colors.secondary};
  border-radius: 6px;
  cursor: pointer;
  background: ${props => props.$checked ? colors.cream : colors.white};
  transition: all 0.2s ease;
  
  @media (min-width: 768px) {
    padding: 15px;
    margin-bottom: 10px;
    border: 2px solid ${props => props.$checked ? colors.primary : colors.secondary};
    border-radius: 8px;
  }

  &:hover {
    border-color: ${colors.primary};
    background: ${colors.cream};
  }
`;

const RadioInput = styled.input`
  margin-right: 8px;
  margin-top: 2px;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    margin-right: 12px;
    margin-top: 0;
  }
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.div`
  font-weight: bold;
  color: ${colors.primary};
  margin-bottom: 3px;
  font-size: 0.95rem;
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 5px;
  }
`;

const OptionDescription = styled.div`
  color: ${colors.brown};
  font-size: 0.75rem;
  line-height: 1.3;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const OptionPrice = styled.div`
  font-weight: bold;
  color: ${colors.primary};
  font-size: 1.1rem;
  flex-shrink: 0;
  text-align: right;
  
  @media (min-width: 768px) {
    font-size: 1.2rem;
  }
`;

const PaymentSelectorTitle = styled.h3`
  margin: 0 0 8px 0;
  color: ${colors.primary};
  font-size: 1rem;
  padding: 0 4px;
  
  @media (min-width: 768px) {
    margin: 0 0 15px 0;
    font-size: 1.1rem;
    padding: 0;
  }
`;

const CardElementContainer = styled.div`
  padding: 15px;
  border: 2px solid ${colors.secondary};
  border-radius: 8px;
  margin: 15px 0;
  background: ${colors.white};
  
  .StripeElement {
    height: 40px;
    padding: 10px 14px;
    color: #424770;
    background-color: white;
    border: 1px solid transparent;
    border-radius: 4px;
    box-shadow: 0 1px 3px 0 #e6ebf1;
    -webkit-transition: box-shadow 150ms ease;
    transition: box-shadow 150ms ease;
  }

  .StripeElement--focus {
    box-shadow: 0 1px 3px 0 #cfd7df;
  }

  .StripeElement--invalid {
    border-color: #fa755a;
  }

  .StripeElement--webkit-autofill {
    background-color: #fefde5 !important;
  }
`;

const PayButton = styled.button`
  width: 100%;
  padding: 15px;
  background: ${colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: ${colors.secondary};
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const StripeInfo = styled.div`
  background: ${colors.cream};
  padding: 8px;
  border-radius: 6px;
  margin: 8px 0;
  font-size: 0.8rem;
  line-height: 1.4;
  color: ${colors.brown};
  border: 1px solid ${colors.secondary};
  
  @media (min-width: 768px) {
    padding: 15px;
    margin: 15px 0;
    font-size: 0.9rem;
    border-radius: 8px;
    border: 2px solid ${colors.secondary};
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  background: #fadad7;
  padding: 10px;
  border-radius: 6px;
  margin: 10px 0;
  font-size: 0.9rem;
  border: 1px solid #e74c3c;
`;

const SuccessMessage = styled.div`
  color: #27ae60;
  background: #d5f7e3;
  padding: 10px;
  border-radius: 6px;
  margin: 10px 0;
  font-size: 0.9rem;
  border: 1px solid #27ae60;
`;

interface StripeFrontendPaymentProps {
  adults: number;
  children: number;
  bicchieri?: number;
  email: string;
  onPaymentSuccess: (paymentDetails: any) => void;
  onPaymentError: (error: string) => void;
}

const PREZZO_BICCHIERE = 5;

const CheckoutForm: React.FC<StripeFrontendPaymentProps> = ({
  adults,
  children,
  bicchieri = 0,
  email,
  onPaymentSuccess,
  onPaymentError
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentType, setPaymentType] = useState<'deposit' | 'full'>('deposit');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Calcolo acconti: tutti pagano 5€ di acconto
  const totalDeposit = paymentService.calculateDepositAmount(adults, children, email);
  
  // Calcolo prezzi totali: adulti e vegetariani 25€, bambini 15€
  const fullTotal = paymentService.calculateFullAmount(adults, children, email);
  
  // Saldo rimanente se si sceglie l'acconto (il bicchiere è già pagato per intero, non rientra qui)
  const remaining = paymentService.calculateRemainingAmount(adults, children, email);

  // Il bicchiere commemorativo si paga sempre per intero, indipendentemente da acconto/completo
  const bicchieriTotal = bicchieri * PREZZO_BICCHIERE;

  // Importo da pagare ora
  const amountToPay = paymentService.calculatePaymentAmount(adults, children, paymentType, email) + bicchieriTotal;

  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe non è caricato correttamente. Ricarica la pagina.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setError('Errore nel recupero dei dati della carta');
      setIsProcessing(false);
      return;
    }

    try {
      console.log('🔄 Iniziando pagamento Stripe...');
      
      // STEP 1: Crea Payment Intent tramite backend
      console.log('📡 Chiamando backend per creare Payment Intent...');
      const response = await fetch(`${configService.getBackendUrl()}/api/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(amountToPay * 100), // Stripe usa centesimi
          currency: 'eur',
          payment_type: paymentType,
          adults,
          children,
          description: `Aria di Fest - ${adults} adulti, ${children} bambini${bicchieri > 0 ? `, ${bicchieri} bicchieri commemorativi` : ''} (${paymentType})`,
          metadata: {
            adults: adults.toString(),
            children: children.toString(),
            payment_type: paymentType,
            event: 'aria-di-fest',
            user_email: email
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const { client_secret } = await response.json();
      console.log('✅ Client secret ricevuto dal backend');

      // STEP 2: Conferma il pagamento con Stripe
      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: 'Cliente Aria di Fest',
            email: 'customer@ariadifest.com',
          },
        }
      });

      console.log('📊 Risultato conferma pagamento:', result);

      if (result.error) {
        console.error('❌ Errore Stripe:', result.error);
        
        // Controlla se è un errore di autenticazione 3DS
        if (result.error.code === 'authentication_required' || 
            result.error.code === 'card_declined' || 
            result.error.type === 'card_error' ||
            result.error.message?.includes('authentication') ||
            result.error.message?.includes('3D Secure')) {
          setError('Non siamo riusciti ad autenticare la tua carta. Prova con un\'altra carta');
        } else {
          setError(result.error.message || 'Errore nel pagamento');
        }
      } else if (result.paymentIntent.status === 'succeeded') {
        console.log('✅ Pagamento completato con successo!');
        setSuccess('Pagamento completato con successo!');
        
        const paymentDetails = {
          paymentIntentId: result.paymentIntent.id,
          amount: amountToPay,
          currency: 'EUR',
          status: result.paymentIntent.status,
          timestamp: new Date().toISOString(),
          paymentType: paymentType,
          isFullPayment: paymentType === 'full',
          paymentMethod: 'stripe',
          stripeResponse: result.paymentIntent
        };
        
        setTimeout(() => {
          onPaymentSuccess(paymentDetails);
        }, 1500);
      } else {
        console.log('⚠️ Payment Intent in stato:', result.paymentIntent.status);
        setError(`Pagamento in stato: ${result.paymentIntent.status}`);
      }
    } catch (err) {
      console.error('❌ Errore nel processamento:', err);
      setError('Errore nel processamento della carta. Riprova.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <StripeContainer>
        <StripeTitle>💳 Pagamento con Carta di Credito</StripeTitle>
        
        <PaymentTypeSelector>
          <PaymentSelectorTitle>💰 Scegli il tipo di pagamento:</PaymentSelectorTitle>
          
          <PaymentOption $checked={paymentType === 'deposit'}>
            <RadioInput 
              type="radio" 
              name="paymentType" 
              value="deposit" 
              checked={paymentType === 'deposit'}
              onChange={(e) => setPaymentType(e.target.value as 'deposit' | 'full')}
            />
            <OptionContent>
              <OptionTitle>🎯 Acconto</OptionTitle>
              <OptionDescription>
                Prenota ora con l'acconto, il resto durante l'evento.
              </OptionDescription>
            </OptionContent>
            <OptionPrice>€{totalDeposit}</OptionPrice>
          </PaymentOption>

          <PaymentOption $checked={paymentType === 'full'}>
            <RadioInput 
              type="radio" 
              name="paymentType" 
              value="full" 
              checked={paymentType === 'full'}
              onChange={(e) => setPaymentType(e.target.value as 'deposit' | 'full')}
            />
            <OptionContent>
              <OptionTitle>✅ Pagamento Completo</OptionTitle>
              <OptionDescription>
                Paga tutto ora ed evita la coda al tuo arrivo
              </OptionDescription>
            </OptionContent>
            <OptionPrice>€{fullTotal}</OptionPrice>
          </PaymentOption>
        </PaymentTypeSelector>
        
        <PaymentSummary>
          <SummaryItem>
            <SummaryLabel>Menù Adulto/Vegetariano ({adults}x):</SummaryLabel>
            <SummaryValue>€{adults * 25}</SummaryValue>
          </SummaryItem>
          {children > 0 && (
            <SummaryItem>
              <SummaryLabel>Menù Bambino ({children}x):</SummaryLabel>
              <SummaryValue>€{children * 15}</SummaryValue>
            </SummaryItem>
          )}
          {bicchieri > 0 && (
            <SummaryItem>
              <SummaryLabel>Bicchieri commemorativi ({bicchieri}x):</SummaryLabel>
              <SummaryValue>€{bicchieriTotal} (pagati per intero)</SummaryValue>
            </SummaryItem>
          )}
          <SummaryItem>
            <SummaryLabel>
              {paymentType === 'full' ? 'Totale da pagare ora:' : 'Acconto da pagare ora:'}
            </SummaryLabel>
            <SummaryValue>€{amountToPay}</SummaryValue>
          </SummaryItem>
          {paymentType === 'deposit' && (
            <SummaryItem style={{ fontSize: '0.9rem', fontWeight: 'normal', borderTop: 'none', paddingTop: '5px' }}>
              <SummaryLabel>Saldo rimanente (da pagare all'evento):</SummaryLabel>
              <SummaryValue>€{remaining}</SummaryValue>
            </SummaryItem>
          )}
        </PaymentSummary>

        <CardElementContainer>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
              hidePostalCode: true, // Nascondi il codice postale se non necessario
            }}
          />
        </CardElementContainer>

        {error && (
          <ErrorMessage>
            ❌ {error}
          </ErrorMessage>
        )}

        {success && (
          <SuccessMessage>
            ✅ {success}
          </SuccessMessage>
        )}

        <StripeInfo>
          🔒 <strong>Pagamento Sicuro con Stripe</strong><br />
          {paymentType === 'full'
            ? `Stai per pagare l'intero importo di €${amountToPay}. Non dovrai pagare nulla all'evento!`
            : `Stai pagando un acconto di €${amountToPay}. Il saldo rimanente di €${remaining} sarà da versare durante l'evento.`
          }<br />
          Dopo il pagamento riceverai conferma via email con tutti i dettagli.
        </StripeInfo>

        <PayButton
          type="submit"
          disabled={!stripe || isProcessing}
        >
          {isProcessing ? (
            paymentType === 'full'
              ? `🔄 Elaborazione pagamento di €${amountToPay}...`
              : `🔄 Elaborazione acconto di €${amountToPay}...`
          ) : (
            paymentType === 'full'
              ? `Paga €${amountToPay} con Carta`
              : `Paga acconto €${amountToPay} con Carta`
          )}
        </PayButton>

        {isProcessing && (
          <div style={{ 
            textAlign: 'center', 
            marginTop: '15px', 
            color: colors.primary,
            fontWeight: 'bold',
            background: colors.cream,
            padding: '15px',
            borderRadius: '8px',
            border: `2px solid ${colors.secondary}`
          }}>
            🔄 Elaborazione pagamento Stripe in corso...<br />
            <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: colors.brown }}>
              {paymentType === 'full'
                ? `Pagamento completo di €${amountToPay} in elaborazione...`
                : `Acconto di €${amountToPay} in elaborazione...`
              }<br />
              Non chiudere questa pagina
            </span>
          </div>
        )}
      </StripeContainer>
    </form>
  );
};

const StripeFrontendPayment: React.FC<StripeFrontendPaymentProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm {...props} />
    </Elements>
  );
};

export default StripeFrontendPayment;
